import React, { useState, useEffect } from "react";
import MonacoEditor from "@monaco-editor/react";
import hcl from "react-syntax-highlighter/dist/esm/languages/prism/hcl";

interface MonacoCodeViewerProps {
  code: string | { [filename: string]: string };
  language?: string;
  height?: string;
  type?: "root" | "module";
}

const MonacoCodeViewer: React.FC<MonacoCodeViewerProps> = ({
  code,
  language = "hcl",
  height = "100%",
  type = "root",
}) => {
  const [selectedFile, setSelectedFile] = useState<string>("");
  const [currentCode, setCurrentCode] = useState<string>("");

  // Handle the editor setup
  function handleEditorWillMount(monaco: any) {
    if (
      !monaco.languages.getLanguages().some((lang: any) => lang.id === "hcl")
    ) {
      monaco.languages.register({ id: "hcl" });
      monaco.languages.setMonarchTokensProvider("hcl", hcl);
    }
  }

  // Determine if we have multiple files and get the list of files
  const isMultiFile = typeof code === "object";
  const files = isMultiFile ? Object.keys(code) : [];

  // Update selected file and current code when code prop changes
  useEffect(() => {
    if (isMultiFile) {
      if (!selectedFile && files.length > 0) {
        setSelectedFile(files[0]);
        setCurrentCode((code as { [key: string]: string })[files[0]]);
      } else if (selectedFile) {
        setCurrentCode((code as { [key: string]: string })[selectedFile]);
      }
    } else {
      setCurrentCode(code as string);
    }
  }, [code, selectedFile, files]);

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      {isMultiFile && files.length > 0 && (
        <div className="flex gap-2 p-2 bg-gray-100 border-b overflow-x-auto shrink-0">
          {files.map((filename) => (
            <button
              key={filename}
              onClick={() => {
                setSelectedFile(filename);
                setCurrentCode((code as { [key: string]: string })[filename]);
              }}
              className={`px-3 py-1 rounded-md text-sm whitespace-nowrap ${
                selectedFile === filename
                  ? "bg-blue-500 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-200"
              }`}
            >
              {filename}
            </button>
          ))}
        </div>
      )}
      <div
        className={`flex-1 min-h-0 bg-codebgLight text-textprimaryLight overflow-hidden ${
          type === "module"
            ? "border-l-4 border-l-gray-400"
            : "border-l-4 border-l-green-400"
        }`}
      >
        <MonacoEditor
          beforeMount={handleEditorWillMount}
          height="100%"
          width="100%"
          defaultLanguage={language}
          value={currentCode}
          theme="vs-light"
          options={{
            readOnly: true,
            minimap: { enabled: false },
            fontSize: 14,
            scrollBeyondLastLine: false,
            wordWrap: "on",
            automaticLayout: true,
            scrollbar: {
              vertical: "auto",
              horizontal: "auto",
              verticalScrollbarSize: 10,
              horizontalScrollbarSize: 10,
            },
            overviewRulerLanes: 0,
            hideCursorInOverviewRuler: true,
            overviewRulerBorder: false,
          }}
        />
      </div>
    </div>
  );
};

export default MonacoCodeViewer;
